import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const CountryContext = createContext();

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

export const CountryProvider = ({ children }) => {
  // Load from localStorage or default to US
  const [selectedCountry, setSelectedCountryState] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedCountry');
      return saved || 'US';
    } catch (error) {
      console.error('Error loading country from localStorage:', error);
      return 'US';
    }
  });

  // Only US and UK supported
  const countries = useMemo(() => [
    { code: 'US', name: 'United States', currency: 'USD', symbol: '$' },
    { code: 'UK', name: 'United Kingdom', currency: 'GBP', symbol: '£' }
  ], []);

  // Save to localStorage whenever country changes
  // ✅ FIX: Memoize with useCallback to prevent infinite re-render loops
  const setSelectedCountry = useCallback((code) => {
    try {
      setSelectedCountryState(code);
      localStorage.setItem('selectedCountry', code);
      console.log('Country saved to localStorage:', code);
    } catch (error) {
      console.error('Error saving country to localStorage:', error);
      setSelectedCountryState(code);
    }
  }, []);

  const getCountryData = useCallback((code) => {
    return countries.find(c => c.code === code) || countries[0];
  }, [countries]);

  const value = useMemo(() => ({
    selectedCountry,
    setSelectedCountry,
    countries,
    currentCountry: getCountryData(selectedCountry),
    getCountryData
  }), [selectedCountry, setSelectedCountry, countries, getCountryData]);

  return (
    <CountryContext.Provider value={value}>
      {children}
    </CountryContext.Provider>
  );
};

export default CountryContext;
